//lib
import { v4 as uuid } from "uuid";
import { useEffect, useState } from "react";
import { EncircledPlusIcon } from "../../../../utils/svg-components";

//src
import { ResourcesColumn, ResourcesColumnCollapse } from "./columns";
import { isSomething } from "../../../../utils/utils";
import Table from "../../../../components/table/table";
import { Button } from "../../../../components/button/button";
import { DEFAULT_RESOURCE } from "../../milestone/utils/budget.utils";
import { ProjectResourcesStyled } from "./project-resources.style";
import { useGetProjectsUsersList } from "../../../../utils/projects-list.utils";

const Resources = (props) => {
  const {
    project,
    resources,
    setResources,
    handleResourceCleanUpInDeliverables,
    handleAddResourceInDeliverables,
    rateOption,
    loggedRolesList,
    setShowDeleteModal,
    isCollapsed,
    fetchedRolesList,
  } = props;
  const { leadsList: usersOptions } = useGetProjectsUsersList();

  const resourceList = Array.isArray(resources) ? resources : [];

  const rolesList = Object.keys(fetchedRolesList);

  const resourcesRolesList = resourceList.map((x) => x?.role);

  const filteredRoles = rolesList?.filter(
    (x) => !resourcesRolesList?.includes(x)
  );

  const rolesOptions = filteredRoles?.map((x) => ({
    label: x,
    value: x,
    category: fetchedRolesList[x].serviceGroup,
  }));

  const updateResource = (id, field, value) => {
    // If we're in flat rate mode and updating the rate field,
    // update all resources with the same value.
    if (rateOption === "flat" && field === "rate") {
      setResources((prev) => {
        const newResources = prev.map((r) => ({
          ...r,
          [field]: Number(value),
        }));
        return newResources;
      });
      return;
    }

    // If we're in flat rate mode and updating the role,
    // update only the matching resource.
    if (
      rateOption === "flat" &&
      field === "role" &&
      isSomething(resources?.[0]?.rate)
    ) {
      setResources((prev) => {
        const newResources = prev.map((r) => {
          if (r.id === id) {
            return {
              ...r,
              [field]: value,
              rate: resources[0].rate,
            };
          }
          return r;
        });
        return newResources;
      });
      return;
    }

    if (
      rateOption === "flat" &&
      field === "role" &&
      !isSomething(resources?.[0]?.rate)
    ) {
      setResources((prev) => {
        const newResources = prev.map((r) => {
          if (r.id === id) {
            return {
              ...r,
              [field]: value,
              rate: "",
            };
          }
          return r;
        });
        return newResources;
      });
      return;
    }

    // Default branch: update only the matching resource.
    setResources((prev) => {
      const newResources = prev.map((r) => {
        if (r.id === id) {
          return {
            ...r,
            [field]: field === "rate" ? Number(value) : value,
            ...(field === "role"
              ? { rate: fetchedRolesList[value]?.rate }
              : {}),
          };
        }
        return r;
      });
      return newResources;
    });
  };

  const addRole = () => {
    const roleToAdd = DEFAULT_RESOURCE(uuid());
    setResources((prev) => [...prev, roleToAdd]);
    handleAddResourceInDeliverables({
      id: roleToAdd?.id,
      hours: 0,
      isEnabled: false,
    });
  };

  const removeRole = (id) => {
    const role = resources?.find((x) => x?.id === id)?.role;

    if (resources?.length === 1) {
      setShowDeleteModal({ id, type: "last-resource" });
      return;
    }
    if (loggedRolesList?.includes(role)) {
      setShowDeleteModal({ id, type: "delete" });
      return;
    }
    setResources((prev) => prev?.filter((x) => x.id !== id));
    handleResourceCleanUpInDeliverables(id);
  };

  useEffect(() => {
    if (isSomething(project)) {
      if (!isSomething(resources)) {
        addRole();
      }
    }
  }, [project]);

  const columns = isCollapsed
    ? ResourcesColumnCollapse({
        rolesOptions,
        updateResource,
        loggedRolesList,
        fetchedRolesList,
      })
    : ResourcesColumn({
        usersOptions,
        rolesOptions,
        removeRole,
        updateResource,
        rateOption,
        loggedRolesList,
        fetchedRolesList,
      });

  return (
    <div className="table-wrap">
      <Table data={resourceList} columns={columns} rowBackground={"#f6f6fe"} />

      <div className="add-btn">
        <Button
          startIcon={<EncircledPlusIcon color="white" />}
          onClick={addRole}
          size="large"
        >
          Add Role
        </Button>
      </div>
    </div>
  );
};

const ProjectResources = ({
  project,
  resources,
  setResources,
  handleAddResourceInDeliverables,
  handleResourceCleanUpInDeliverables,
  rateOption,
  setShowDeleteModal,
  loggedRolesList,
  isCollapsed,
  fetchedRolesList,
}) => {
  return (
    <ProjectResourcesStyled isCollapsed={isCollapsed}>
      <Resources
        project={project}
        resources={resources}
        setResources={setResources}
        handleResourceCleanUpInDeliverables={
          handleResourceCleanUpInDeliverables
        }
        handleAddResourceInDeliverables={handleAddResourceInDeliverables}
        rateOption={rateOption}
        loggedRolesList={loggedRolesList}
        setShowDeleteModal={setShowDeleteModal}
        isCollapsed={isCollapsed}
        fetchedRolesList={fetchedRolesList}
      />
    </ProjectResourcesStyled>
  );
};

export default ProjectResources;
